import {
  Component,
  Injector,
  Input,
  OnInit,
  ɵcreateInjector as createInjector,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ParseJson } from 'ZalamoLibrary/src/lib/core/decorators/ParseJson';
import { UserWallEvent } from '../../../../../../../../graphql/libs/viewer-wall/types';
import { UserWallEventType } from '../../graphql';
import {
  ReplaySubject,
  combineLatest,
  first,
  map,
  of,
  pluck,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { ImagesContainer } from 'Upload/upload/graphql';
import { ZalamoDialogService } from 'ZalamoLibrary/src/lib/ui/zalamo-dialog/zalamo-dialog.service';
import { GetEventDetailsGQL } from 'ZalamoLibrary/src/lib/ui/events-history-stream/graphql';
import { GetAvailableImagesInContextGQL } from 'ZalamoLibrary/src/lib/ui/image-management/graphql';
import { QuickImageListPreviewComponent } from '../../../quick-previews/quick-image-list-preview/quick-image-list-preview.component';
import { ZalamoObject } from '../../../../../graphql';

@Component({
  selector: 'app-photographer-wall-order-placed',
  templateUrl: './photographer-wall-order-placed.component.html',
  styleUrls: ['./photographer-wall-order-placed.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PhotographerWallOrderPlacedComponent implements OnInit {
  @Input()
  @ParseJson
  details: UserWallEvent[UserWallEventType.OrderPlaced];

  images$ = of(null).pipe(
    map(() => this.details),
    switchMap(({ context, contextId }) =>
      this.getAvailableImagesInContextGQL
        .fetch({
          variables: {
            context: context as any as ImagesContainer,
            contextId,
            includeDeleted: true,
          },
        })
        .pipe(pluck('data', 'getAvailableGroupsInContext')),
    ),
  );
  contextImages$ = this.images$.pipe(
    map((allImages) => {
      const grouped = allImages.reduce((prev, current) => {
        for (const image of current.images.files) {
          prev[image.photo.photoId] = {
            photoGroupId: current.photoGroupId,
            ratio: image.photo.width / image.photo.height,
          };
        }
        return prev;
      }, {});
      return this.details.images.reduce((previous, current) => {
        previous.push({
          photoId: current,
          photoGroupId: grouped[current].photoGroupId,
          aspect_ratio: grouped[current].ratio,
        });
        return previous;
      }, []);
    }),
    shareReplay(1),
  );
  avisibleImages$ = this.contextImages$.pipe(
    map((allImages) => {
      if (allImages.length > 9) {
        return allImages.slice(0, 8);
      } else {
        return allImages;
      }
    }),
  );
  ob: ZalamoObject = ZalamoObject.OrganizationClient;
  privateSessionContext = ImagesContainer.PrivateSession;
  private _contextCreationDate = new ReplaySubject<number>(1);
  private _eventId = new ReplaySubject<string>(1);
  orderDetails$ = combineLatest([
    this._contextCreationDate,
    this._eventId,
  ]).pipe(
    switchMap(([contextCreationDate, eventId]) =>
      this.getEventDetailsGQL
        .fetch({
          variables: {
            contextCreationDate,
            eventId,
          },
        })
        .pipe(
          pluck('data', 'getEventDetails', 'details'),
          map((d) => JSON.parse(d)),
        ),
    ),
    shareReplay(1),
  );

  @Input() set contextCreationDate(value) {
    this._contextCreationDate.next(value);
  }

  @Input() set eventId(value) {
    this._eventId.next(value);
  }

  constructor(
    private zalamoDialogService: ZalamoDialogService,
    private injector: Injector,
    private getEventDetailsGQL: GetEventDetailsGQL,
    private getAvailableImagesInContextGQL: GetAvailableImagesInContextGQL,
  ) {}

  ngOnInit(): void {}

  async preview() {
    const { OrdersModule } =
      await import('../../../../../../../zalamo-library/src/lib/ui/orders/orders.module');
    const injector = createInjector(OrdersModule, this.injector);
    const carouselModule = injector.get(OrdersModule);
    carouselModule.openOrder(this.details.orderId);
  }

  showImages(index: number) {
    this.contextImages$
      .pipe(
        first(),
        tap((images) => {
          const ref = this.zalamoDialogService.openFull(
            QuickImageListPreviewComponent,
            { isBare: true },
          );
          ref.componentInstance.allPhotos = images;
          ref.componentInstance.currentIndex = index;
          ref.componentInstance.closed
            .pipe(
              takeUntil(ref.afterClosed()),
              tap(() => ref.close()),
            )
            .subscribe();
        }),
      )
      .subscribe();
  }
}
